import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom'; // Added Link for clean navigation paths
import { Firebase, firebase } from '../firebase/config';
import './MyOrders.css';

const MyOrders = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const currentUser = Firebase.auth().currentUser;
    if (!currentUser) {
      alert("Please login to view your orders.");
      history.push('/login');
      return;
    }

    const unsubscribe = Firebase.firestore()
      .collection('transactions')
      .where('buyerId', '==', currentUser.uid)
      .orderBy('createdAt', 'desc')
      .onSnapshot(async (snapshot) => {
        try {
          const orderList = [];

          for (const doc of snapshot.docs) {
            const transactionData = doc.data();
            let productData = null;

            if (transactionData.productId) {
              const productDoc = await Firebase.firestore()
                .collection('products')
                .doc(transactionData.productId)
                .get();

              if (productDoc.exists) {
                productData = productDoc.data();
              }
            }

            orderList.push({
              id: doc.id,
              ...transactionData,
              product: productData
            });
          }

          setOrders(orderList);
          setLoading(false);
        } catch (error) {
          console.error("Error aggregating orders and product matrices:", error);
          setLoading(false);
        }
      }, (error) => {
        console.error("Firestore real-time listener error:", error);
        setLoading(false);
      });

    return () => unsubscribe();
  }, [history]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'awaiting_payment':
        return <span className="order-badge badge-warning">Awaiting Payment</span>;
      case 'escrow_held':
        return <span className="order-badge badge-success">Secured in Escrow</span>;
      case 'shipped':
        return <span className="order-badge badge-info">Shipped</span>;
      case 'completed':
        return <span className="order-badge badge-primary">Completed</span>;
      case 'cancelled':
        return <span className="order-badge badge-danger">Cancelled</span>;
      default:
        return <span className="order-badge badge-secondary">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="orders-loading-container">
        <div className="orders-loading-text">Loading your ClubLoop order history...</div>
      </div>
    );
  }

  return (
    <div className="orders-container">

      {/* 1. BACK TO HOME LINK AT TOP */}
      <div className="orders-navigation-top">
        <Link to="/" className="orders-back-link">
          ← Back to Clubhouse Marketplace
        </Link>
      </div>

      <h1 className="orders-main-header">My Purchases</h1>

      {orders.length === 0 ? (
        <div className="orders-empty-state">
          <h3>You haven't purchased any equipment yet.</h3>
          <button className="orders-btn-browse" onClick={() => history.push('/')}>
            Explore Clubhouse Marketplace
          </button>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="order-card">

              {/* Card Header */}
              <div className="order-card-header">
                <div>
                  <p className="order-meta-label">ORDER PLACED</p>
                  <p className="order-meta-value">
                    {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('en-IN') : 'Processing...'}
                  </p>
                </div>
                <div>
                  <p className="order-meta-label">TOTAL PAID</p>
                  <p className="order-meta-value">₹{Number(order.totalAmount).toLocaleString('en-IN')}</p>
                </div>
                <div className="order-header-right">
                  <p className="order-meta-label">TRANSACTION ID</p>
                  <p className="order-meta-value id-text">{order.id}</p>
                </div>
              </div>

              {/* Card Body */}
              <div className="order-card-body">
                <div className="order-product-row">
                  <img
                    src={order.product?.url || order.product?.images?.[0] || 'https://via.placeholder.com/150'}
                    alt={order.product?.name || 'Golf Equipment'}
                    className="order-product-image"
                  />
                  <div className="order-product-details">
                    <h3 className="order-product-title">{order.product?.name || order.product?.title || 'Unknown Product'}</h3>
                    <p className="order-product-category">{order.product?.category || 'Golf Equipment'}</p>
                    <div className="order-status-wrapper">
                      Status: {getStatusBadge(order.status)}
                    </div>
                  </div>

                  {/* 2. DYNAMIC PREVIEW ACTION LINK ON THE RIGHT */}
                  <div className="order-action-column">
                    {order.productId ? (
                      <Link to={`/ad/${order.productId}`} className="order-preview-btn">
                        View Product Listing
                      </Link>
                    ) : (
                      <span className="order-text-muted">Listing Unavailable</span>
                    )}
                  </div>

                </div>
              </div>

              {/* Card Footer */}
              {order.status === 'escrow_held' && (
                <div className="order-card-footer escrow-alert">
                  🛡️ <strong>ClubLoop Protection Active:</strong> Your payment of ₹{Number(order.totalAmount).toLocaleString('en-IN')} is being held securely in escrow. Funds will only be released to the seller after the shipment arrives and you verify the equipment condition.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
